/**
 * The family's memory book on the memorial page (PRO-4415). The API decides whether it
 * is for sale; the page asks through our REST route (`MemoryBookOffer.php`, cached
 * briefly). Buying and the payment status go straight to the API's public routes, which
 * limit per visitor, so one visitor's tries never block another's.
 */
import { sprintf } from "sprintf-js";
import api from "../api";

/** Stripe sends the payer back with this parameter (the API adds it to `return_url`). */
export const RETURN_PARAM = "memory_book_checkout";

const POLL_MS = 2000;
const POLL_TIMEOUT_MS = 30_000;

export type BookSizeId = "S" | "L";

export interface BookImage {
  height: number;
  /** Twice the size (3200 px wide), for the large view; missing until the API makes it. */
  large?: string;
  /** Half the size (800 px wide), for phones; missing until the API makes it. */
  small?: string;
  url: string;
  width: number;
}

export interface BookSize {
  labelMm: string;
  size: BookSizeId;
  unitPrice: number;
}

export interface MemoryBook {
  /** The back cover, the same size as the front; null until the API makes it. */
  back: BookImage | null;
  cover: BookImage;
  currency: string;
  deliveryDays: { max: number; min: number };
  /** The book in someone's hands, for the card; null until the API has made it. */
  lifestyle: BookImage | null;
  pages: number;
  publishableKey: string;
  shareId: string;
  /** One fee per order, whatever the quantity. */
  shipping: number;
  sizes: BookSize[];
  /** One image per spread, in order. */
  spreads: BookImage[];
}

/** The schedule will put the family's book on sale on this page; `shareAt` is null once that time has passed. */
export interface UpcomingBook {
  shareAt: string | null;
}

/** What our REST route answers: the book for sale, or, while there is none, one on its way. */
export interface MemoryBookOffer {
  book: MemoryBook | null;
  /** The ask itself failed (offline, the API down): no answer, rather than "no book". */
  failed?: true;
  upcoming: UpcomingBook | null;
}

/** Why the API refused a discount code (§7), or why it couldn't be checked. */
export type CouponErrorCode =
  | "NOT_FOUND_ERROR"
  | "NOT_APPLICABLE_ERROR"
  | "EXPIRED_ERROR"
  | "MAX_USES_ERROR"
  | "WRONG_CURRENCY_ERROR"
  | "MINIMUM_AMOUNT_ERROR"
  | "rate_limited"
  | "unavailable";

const COUPON_ERRORS: CouponErrorCode[] = [
  "NOT_FOUND_ERROR",
  "NOT_APPLICABLE_ERROR",
  "EXPIRED_ERROR",
  "MAX_USES_ERROR",
  "WRONG_CURRENCY_ERROR",
  "MINIMUM_AMOUNT_ERROR",
];

/**
 * A discount code as the API answered it (PRO-4618). The API does the maths: a percentage
 * gives each size's price of one copy after it; an amount is taken off the order in Stripe.
 */
export interface BookCoupon {
  /** The stored form ("SOMMAR"), or what was typed when it is unknown. */
  code: string;
  /** Null when the code is valid. */
  error: CouponErrorCode | null;
  type: "percent" | "amount" | null;
  /** One copy's price after a percentage, per size; missing for an amount. */
  unitPrices: Partial<Record<BookSizeId, number>>;
  /** The percentage, or the amount in the book's currency. */
  value: number;
}

export type CheckoutStatus = "paid" | "pending" | "expired" | "unknown";

/** What the payer just typed into Stripe, for the thank-you; each field may be missing. */
export interface Receipt {
  city?: string;
  copies?: number;
  /** For a return visit where the book's card is not loaded (§7 v4). */
  coverUrl?: string;
  currency?: string;
  deliveryDays?: { max: number; min: number };
  emailMasked?: string;
  size?: BookSizeId;
  /** "270 × 270 mm", as the API writes it. */
  sizeLabel?: string;
  total?: number;
}

export interface PaymentStatus {
  /** The buyer's order in Mina sidor, once paid; '' without one. */
  appLink: string;
  receipt: Receipt | null;
  status: CheckoutStatus;
}

/** Why a purchase could not start, by the API's error codes; `failed` is anything else. */
export type PurchaseErrorCode =
  | "not_for_sale"
  | "share_changed"
  | "rate_limited"
  | "checkout_unavailable"
  | "coupon_invalid"
  | "failed";

export class PurchaseError extends Error {
  readonly code: PurchaseErrorCode;
  /** Why the discount code was refused when paying; `unavailable` when Stripe refused it. */
  readonly couponError: CouponErrorCode | null;

  constructor(
    code: PurchaseErrorCode,
    options?: ErrorOptions & { couponError?: CouponErrorCode }
  ) {
    super(code, options);
    this.code = code;
    this.couponError = options?.couponError ?? null;
  }
}

export interface Checkout {
  clientSecret: string;
  publishableKey: string;
}

type Raw = Record<string, unknown>;

const isRecord = (value: unknown): value is Raw =>
  !!value && typeof value === "object" && !Array.isArray(value);

function parseImage(raw: unknown): BookImage | null {
  if (!(isRecord(raw) && typeof raw.url === "string" && raw.url)) {
    return null;
  }
  const https = (value: unknown) =>
    typeof value === "string" && value.startsWith("https://")
      ? value
      : undefined;
  return {
    height: Number(raw.height) || 0,
    large: https(raw.url_2x),
    small: https(raw.url_small),
    url: raw.url,
    width: Number(raw.width) || 0,
  };
}

/** Where the book sits in the lifestyle photo (1536 × 1024), as imgix's focal point. */
const LIFESTYLE_FOCUS = { x: 702 / 1536, y: 512 / 1024 };

/**
 * The lifestyle picture cropped around the book, not the photo's centre: a little closer
 * on phones, where the card is small. `url` is the API's imgix URL.
 */
export function lifestyleUrl(
  url: string,
  options: { ratio: string; width: number; zoom: number }
): string {
  try {
    const u = new URL(url);
    for (const [key, value] of Object.entries({
      ar: options.ratio,
      auto: "format,compress",
      crop: "focalpoint",
      fit: "crop",
      "fp-x": LIFESTYLE_FOCUS.x.toFixed(3),
      "fp-y": LIFESTYLE_FOCUS.y.toFixed(3),
      "fp-z": String(options.zoom),
      w: String(options.width),
    })) {
      u.searchParams.set(key, value);
    }
    return u.toString();
  } catch {
    return url;
  }
}

function parseSize(raw: unknown): BookSize | null {
  if (!(isRecord(raw) && (raw.size === "S" || raw.size === "L"))) {
    return null;
  }
  return {
    labelMm: String(raw.label_mm ?? ""),
    size: raw.size,
    unitPrice: Number(raw.unit_price) || 0,
  };
}

/** Our REST route's `book`, or null for anything that can't be sold from. */
export function parseBook(raw: unknown): MemoryBook | null {
  if (!isRecord(raw)) {
    return null;
  }
  const cover = parseImage(raw.cover);
  const sizes = (Array.isArray(raw.sizes) ? raw.sizes : [])
    .map(parseSize)
    .filter((size): size is BookSize => size !== null);
  if (
    !(cover && sizes.length && raw.share_id && raw.publishable_key) ||
    typeof raw.currency !== "string"
  ) {
    return null;
  }
  const delivery = isRecord(raw.delivery_estimate) ? raw.delivery_estimate : {};
  return {
    back: parseImage(raw.back),
    cover,
    currency: raw.currency,
    deliveryDays: {
      max: Number(delivery.max_business_days) || 0,
      min: Number(delivery.min_business_days) || 0,
    },
    lifestyle: parseImage(raw.lifestyle),
    pages: Number(raw.pages) || 0,
    publishableKey: String(raw.publishable_key),
    shareId: String(raw.share_id),
    shipping: Number(raw.shipping) || 0,
    sizes,
    spreads: (Array.isArray(raw.previews) ? raw.previews : [])
      .map(parseImage)
      .filter((image): image is BookImage => image !== null),
  };
}

/** Our REST route's `upcoming`, or null when no book is on its way. */
export function parseUpcoming(raw: unknown): UpcomingBook | null {
  if (!isRecord(raw)) {
    return null;
  }
  const shareAt =
    typeof raw.share_at === "string" && !Number.isNaN(Date.parse(raw.share_at))
      ? raw.share_at
      : null;
  return { shareAt };
}

function couponError(raw: unknown): CouponErrorCode {
  return COUPON_ERRORS.find((code) => code === raw) ?? "unavailable";
}

/** The card's `coupon` (§6), or null without one. */
export function parseCoupon(raw: unknown): BookCoupon | null {
  if (!(isRecord(raw) && typeof raw.code === "string")) {
    return null;
  }
  const valid = raw.valid === true;
  const type =
    raw.type === "percent" || raw.type === "amount" ? raw.type : null;
  const unitPrices: BookCoupon["unitPrices"] = {};
  for (const row of Array.isArray(raw.sizes) ? raw.sizes : []) {
    const price = isRecord(row) ? row.unit_price_after_discount : null;
    if (
      isRecord(row) &&
      (row.size === "S" || row.size === "L") &&
      typeof price === "number" &&
      Number.isFinite(price)
    ) {
      unitPrices[row.size] = price;
    }
  }
  return {
    code: raw.code,
    error: valid && type ? null : couponError(raw.error),
    type: valid ? type : null,
    unitPrices: valid ? unitPrices : {},
    value: Number(raw.value) || 0,
  };
}

/**
 * Checks a discount code against the page's book, straight with the API (it isn't cached
 * like the card). Never throws: a failure is a coupon that couldn't be checked.
 */
export async function checkCoupon(options: {
  apiUrl: string;
  code: string;
  orderId: string;
}): Promise<BookCoupon> {
  const failed = (error: CouponErrorCode): BookCoupon => ({
    code: options.code,
    error,
    type: null,
    unitPrices: {},
    value: 0,
  });
  try {
    const response = await fetch(
      `${options.apiUrl}/memory-book/public/orders/${encodeURIComponent(options.orderId)}/book?coupon_code=${encodeURIComponent(options.code)}`,
      { headers: { Accept: "application/json" } }
    );
    if (!response.ok) {
      return failed(response.status === 429 ? "rate_limited" : "unavailable");
    }
    const body: unknown = await response.json();
    const book =
      isRecord(body) && isRecord(body.data) && isRecord(body.data.book)
        ? body.data.book
        : null;
    return parseCoupon(book?.coupon) ?? failed("unavailable");
  } catch {
    return failed("unavailable");
  }
}

/** The book for sale on this memorial page, or null; never throws. */
export async function fetchMemoryBook(
  postId: string,
  fresh = false
): Promise<MemoryBookOffer> {
  try {
    const answer = await api.get(
      `memory-book/${encodeURIComponent(postId)}${fresh ? "?fresh=1" : ""}`
    );
    const book = parseBook(isRecord(answer) ? answer.book : null);
    return {
      book,
      upcoming:
        book || !isRecord(answer) ? null : parseUpcoming(answer.upcoming),
    };
  } catch {
    return { book: null, failed: true, upcoming: null };
  }
}

/** `date` in the page's language and the ceremony's time zone, or the visitor's when either fails. */
function formatIn(
  date: Date,
  options: Intl.DateTimeFormatOptions,
  timeZone: string
): string {
  try {
    return new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
      ...options,
      timeZone: timeZone || undefined,
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat(undefined, options).format(date);
  }
}

/** The calendar day (YYYY-MM-DD) `date` falls on in the time zone ('' for the visitor's), `days` later. */
function calendarDay(date: Date, timeZone: string, days = 0): string {
  const options: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "numeric",
    year: "numeric",
  };
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat("en", {
      ...options,
      timeZone: timeZone || undefined,
    }).formatToParts(date);
  } catch {
    parts = new Intl.DateTimeFormat("en", options).formatToParts(date);
  }
  const part = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value);
  return new Date(Date.UTC(part("year"), part("month") - 1, part("day") + days))
    .toISOString()
    .slice(0, 10);
}

/**
 * When the book can be ordered: "today from 09.00", "tomorrow from 09.00", or "from 14 November
 * at 09.00", always with the time (the schedule's one sale time), all in the ceremony's time zone ('' for the visitor's). On the sale day itself,
 * the date alone read as if the sale were late.
 */
export function saleWhen(
  shareAt: string,
  texts: Record<string, string>,
  timeZone = "",
  now = new Date()
): string {
  const date = new Date(shareAt);
  const day = calendarDay(date, timeZone);
  // "09.00" as the app writes it, except in English.
  const clock = formatIn(
    date,
    { hour: "2-digit", minute: "2-digit" },
    timeZone
  );
  const time = document.documentElement.lang.startsWith("en")
    ? clock
    : clock.replace(":", ".");

  if (day === calendarDay(now, timeZone)) {
    return sprintf(texts.upcomingToday, time);
  }
  if (day === calendarDay(now, timeZone, 1)) {
    return sprintf(texts.upcomingTomorrow, time);
  }
  return sprintf(
    texts.upcomingFrom,
    formatIn(date, { day: "numeric", month: "long" }, timeZone),
    time
  );
}

/** "200 × 200" (mm, as the API sends it) as "20 × 20" (cm, as the app shows it). */
export function sizeInCm(labelMm: string): string {
  return labelMm.replace(/\d+(?:[.,]\d+)?/g, (mm) =>
    String(Number(mm.replace(",", ".")) / 10).replace(".", ",")
  );
}

export function lowestPrice(book: MemoryBook): number {
  return Math.min(...book.sizes.map((size) => size.unitPrice));
}

/** Whole units without decimals ("395 kr", "295 kr."); öre and øre when there are any. */
export function formatPrice(
  amount: number,
  currency: string,
  locale = document.documentElement.lang || "sv-SE"
): string {
  const whole = Number.isInteger(amount);
  try {
    return new Intl.NumberFormat(locale, {
      currency,
      maximumFractionDigits: whole ? 0 : 2,
      minimumFractionDigits: whole ? 0 : 2,
      style: "currency",
    }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
}

/** This page without its query or hash, for Stripe to send the payer back to. */
export function returnUrl(location: Location = window.location): string {
  return `${location.origin}${location.pathname}`;
}

/** The API knows the page by its funeral order (`_order_id`). */
function checkoutUrl(apiUrl: string, orderId: string): string {
  return `${apiUrl}/memory-book/public/orders/${encodeURIComponent(orderId)}/book/checkout`;
}

function errorCode(status: number, code: unknown): PurchaseErrorCode {
  if (status === 429) {
    return "rate_limited";
  }
  if (
    code === "not_for_sale" ||
    code === "share_changed" ||
    code === "checkout_unavailable" ||
    code === "coupon_invalid"
  ) {
    return code;
  }
  return "failed";
}

/**
 * Opens an embedded Stripe Checkout for the size; Stripe asks the quantity, address and email,
 * and shows the discount and total when a code is given.
 */
export async function startPurchase(options: {
  apiUrl: string;
  couponCode?: string;
  orderId: string;
  shareId: string;
  size: BookSizeId;
}): Promise<Checkout> {
  let response: Response;
  try {
    response = await fetch(checkoutUrl(options.apiUrl, options.orderId), {
      body: JSON.stringify({
        ...(options.couponCode ? { coupon_code: options.couponCode } : {}),
        return_url: returnUrl(),
        share_id: options.shareId,
        size: options.size,
      }),
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      method: "POST",
    });
  } catch (cause) {
    throw new PurchaseError("failed", { cause });
  }

  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const error = isRecord(body) ? body.error : null;
    const code = errorCode(response.status, error);
    throw new PurchaseError(code, {
      couponError:
        code === "coupon_invalid" && isRecord(body)
          ? couponError(body.coupon_error)
          : undefined,
    });
  }

  const checkout =
    isRecord(body) && isRecord(body.data) && isRecord(body.data.checkout)
      ? body.data.checkout
      : null;
  if (
    !(
      checkout &&
      typeof checkout.client_secret === "string" &&
      typeof checkout.publishable_key === "string"
    )
  ) {
    throw new PurchaseError("failed");
  }
  return {
    clientSecret: checkout.client_secret,
    publishableKey: checkout.publishable_key,
  };
}

export async function checkoutStatus(
  apiUrl: string,
  sessionId: string
): Promise<PaymentStatus> {
  const response = await fetch(
    `${apiUrl}/memory-book/public/checkout-sessions/${encodeURIComponent(sessionId)}`,
    { headers: { Accept: "application/json" } }
  );
  if (!response.ok) {
    throw new Error(`status ${response.status}`);
  }
  const body: unknown = await response.json();
  const data = isRecord(body) && isRecord(body.data) ? body.data : {};
  const { status } = data;
  return {
    appLink: safeLink(data.app_link),
    receipt: parseReceipt(data.receipt),
    status:
      status === "paid" ||
      status === "pending" ||
      status === "expired" ||
      status === "unknown"
        ? status
        : "unknown",
  };
}

function parseReceipt(raw: unknown): Receipt | null {
  if (!isRecord(raw)) {
    return null;
  }
  const text = (value: unknown) =>
    typeof value === "string" && value.trim() ? value.trim() : undefined;
  const count = (value: unknown) =>
    typeof value === "number" && Number.isFinite(value) && value > 0
      ? value
      : undefined;
  const delivery = isRecord(raw.delivery_estimate)
    ? raw.delivery_estimate
    : null;
  const min = count(delivery?.min_business_days);
  const max = count(delivery?.max_business_days);
  return {
    city: text(raw.city),
    copies: count(raw.copies),
    coverUrl: safeLink(raw.cover_url) || undefined,
    currency: text(raw.currency),
    deliveryDays: min && max ? { max, min } : undefined,
    emailMasked: text(raw.email_masked),
    size: raw.size === "S" || raw.size === "L" ? raw.size : undefined,
    sizeLabel: text(raw.size_label),
    total: count(raw.total),
  };
}

/** Only an https link goes into the page. */
function safeLink(raw: unknown): string {
  if (typeof raw !== "string") {
    return "";
  }
  try {
    return new URL(raw).protocol === "https:" ? raw : "";
  } catch {
    return "";
  }
}

/**
 * The webhook is the only confirmation: ask every 2 s for up to 30 s until it is paid,
 * expired or unknown. Still pending (or unreachable) after that answers `pending`.
 */
export async function waitForPayment(
  apiUrl: string,
  sessionId: string,
  options: {
    intervalMs?: number;
    isCancelled?: () => boolean;
    timeoutMs?: number;
  } = {}
): Promise<PaymentStatus> {
  const interval = options.intervalMs ?? POLL_MS;
  const deadline = Date.now() + (options.timeoutMs ?? POLL_TIMEOUT_MS);
  for (;;) {
    let answer: PaymentStatus = {
      appLink: "",
      receipt: null,
      status: "pending",
    };
    try {
      // biome-ignore lint/performance/noAwaitInLoops: each ask waits for the previous answer.
      answer = await checkoutStatus(apiUrl, sessionId);
    } catch {
      // Ask again: the status route limits per visitor and can be briefly away.
    }
    if (answer.status !== "pending" || options.isCancelled?.()) {
      return answer;
    }
    if (Date.now() + interval > deadline) {
      return answer;
    }
    await new Promise((resolve) => setTimeout(resolve, interval));
  }
}

/** The session Stripe sent the payer back with, removed from the address so a reload doesn't reopen it. */
export function takeReturnedSession(
  location: Location = window.location,
  history: History = window.history
): string | null {
  const url = new URL(location.href);
  const session = url.searchParams.get(RETURN_PARAM);
  if (!session) {
    return null;
  }
  url.searchParams.delete(RETURN_PARAM);
  history.replaceState(history.state, "", url.toString());
  return session;
}
